import { DomainError } from '../errors.js';

/**
 * Pluggable site engines (ADR-0004). `mini_cms` is ConvertStudio, a static site
 * generator: it has no database at runtime, which the backup format must respect.
 * `external` observes a site the Control Plane does not manage yet (ADR-0003).
 */
export const ENGINES = ['wordpress', 'mini_cms', 'external'] as const;
export type Engine = (typeof ENGINES)[number];

/** The one contract every engine adapter implements (TZ section 3). */
export const ENGINE_OPERATIONS = [
  'validate',
  'provision',
  'deploy',
  'backup',
  'restore',
  'clone',
  'migrate',
  'healthCheck',
  'collectMetadata',
  'adminLink',
  'destroy',
] as const;
export type EngineOperation = (typeof ENGINE_OPERATIONS)[number];

export interface EngineCapabilities {
  readonly engine: Engine;
  /** Operations this engine actually implements. */
  readonly supports: ReadonlySet<EngineOperation>;
  /** False for engines whose runtime has no database, such as a static generator. */
  readonly hasDatabase: boolean;
}

const observationOnly: readonly EngineOperation[] = ['healthCheck', 'collectMetadata', 'adminLink'];

export const ENGINE_CAPABILITIES: Record<Engine, EngineCapabilities> = {
  wordpress: {
    engine: 'wordpress',
    supports: new Set(ENGINE_OPERATIONS),
    hasDatabase: true,
  },
  mini_cms: {
    engine: 'mini_cms',
    // No `migrate`: a static release is re-delivered to another target, not moved.
    supports: new Set(ENGINE_OPERATIONS.filter((operation) => operation !== 'migrate')),
    hasDatabase: false,
  },
  external: {
    engine: 'external',
    supports: new Set(observationOnly),
    hasDatabase: false,
  },
};

export function supportsOperation(engine: Engine, operation: EngineOperation): boolean {
  return ENGINE_CAPABILITIES[engine].supports.has(operation);
}

/**
 * Guards the mutating side of the adapter contract. An unsupported operation must
 * fail loudly rather than return a fake success (ADR-0004) — a silent no-op on
 * `destroy` or `backup` is how a site is believed safe while it is not.
 */
export function assertOperationSupported(engine: Engine, operation: EngineOperation): void {
  if (!supportsOperation(engine, operation)) {
    throw new DomainError(
      'ENGINE_OPERATION_UNSUPPORTED',
      `Engine ${engine} does not support ${operation}`,
      { engine, operation },
    );
  }
}
